/**
 * Division 7 Filter Schemas
 *
 * Each item type has a list of item-specific filter definitions.
 * Common filters (search, manufacturer, country, status, hasSubmittals)
 * are auto-included by the filter engine — no need to define them here.
 *
 * Filter types:
 *   select       – single-value dropdown, options derived from data
 *   arrayIncludes – product field is an array, filter checks membership
 *   computed      – uses extractFn to derive a filterable string from product
 */

// --- helpers ---------------------------------------------------------------
const get = (obj, path) =>
  path.split('.').reduce((acc, key) => acc?.[key], obj);

const unique = (products, path) =>
  [...new Set(products.map(p => get(p, path)).filter(Boolean))].sort();

const uniqueFromArrays = (products, path) =>
  [...new Set(products.flatMap(p => get(p, path) || []).filter(Boolean))].sort();

// --- Item-specific schemas -------------------------------------------------
export const ITEM_FILTER_SCHEMAS = {
  'thermal-insulation': [
    {
      field: 'insulationTypes', type: 'computed', label: 'Insulation Type',
      extractFn: p => (p.insulationTypes || []).flatMap(g => (g.items || g.types || []).map(i => `${g.group} — ${i}`)),
      extractOptions: products => {
        const all = products.flatMap(p => (p.insulationTypes || []).flatMap(g => (g.items || g.types || []).map(i => `${g.group} — ${i}`)));
        return [...new Set(all)].sort();
      },
      matchFn: (product, value) => {
        const labels = (product.insulationTypes || []).flatMap(g => (g.items || g.types || []).map(i => `${g.group} — ${i}`));
        return labels.includes(value);
      },
    },
    { field: 'fireCompliance.fireRating', type: 'select', label: 'Fire Rating' },
    { field: 'moistureProperties.applicationArea', type: 'arrayIncludes', label: 'Application Area' },
  ],

  'air-barriers': [
    { field: 'airBarrierType', type: 'select', label: 'Air Barrier Type' },
    { field: 'installationParameters.applicationMethod', type: 'select', label: 'Application Method' },
    { field: 'testingVerification.mockupRequired', type: 'select', label: 'Mock-up Required' },
    { field: 'testingVerification.passFailStatus', type: 'select', label: 'Pass / Fail Status' },
  ],

  'weather-barriers': [
    { field: 'wrbType', type: 'select', label: 'WRB Type' },
    { field: 'scopeCoverage', type: 'select', label: 'Scope Coverage' },
    { field: 'waterResistance.drainagePlaneCapability', type: 'select', label: 'Water Resistance' },
    { field: 'durabilityExposure.uvExposureLimit', type: 'select', label: 'UV Exposure Rating' },
  ],

  'roofing': [
    { field: 'productRole', type: 'select', label: 'Product Role' },
    { field: 'roofingType', type: 'select', label: 'Roofing Type' },
    { field: 'applicationArea', type: 'arrayIncludes', label: 'Application Area' },
    { field: 'fireCompliance.fireRating', type: 'select', label: 'Fire Rating' },
    { field: 'performance.pondingWaterResistance', type: 'select', label: 'Ponding Water Resistance' },
  ],

  'flashing': [
    { field: 'productRole', type: 'arrayIncludes', label: 'Product Role' },
    { field: 'applicationArea', type: 'arrayIncludes', label: 'Application Area' },
    { field: 'metalType', type: 'select', label: 'Metal Type' },
    { field: 'fabricationMethod', type: 'select', label: 'Fabrication Method' },
    { field: 'fireCodeCompliance.nfpa285Compatibility', type: 'select', label: 'NFPA 285 Compatibility' },
  ],

  'joint-protection': [
    { field: 'jointType', type: 'select', label: 'Joint Type' },
    { field: 'location', type: 'arrayIncludes', label: 'Location' },
    { field: 'movementCapability.movementType', type: 'select', label: 'Movement Type' },
    { field: 'environmentalExposure.uvExposureRating', type: 'select', label: 'UV Exposure Rating' },
    { field: 'fireLifeSafety.fireResistiveContinuity', type: 'select', label: 'Fire-resistive Continuity' },
    { field: 'environmentalExposure.waterResistance', type: 'select', label: 'Water Resistance' },
    { field: 'substrateCompatibility.primerRequired', type: 'select', label: 'Primer Required' },
    { field: 'installationParameters.backerRodRequired', type: 'select', label: 'Backer Rod Required' },
  ],
};

/**
 * Derive dynamic options for a filter from the current products array.
 */
export const getFilterOptions = (filter, products) => {
  if (filter.extractOptions) return filter.extractOptions(products);
  if (filter.type === 'arrayIncludes') return uniqueFromArrays(products, filter.field);
  return unique(products, filter.field);
};

/**
 * Test whether a single product matches a single active filter value.
 */
export const matchFilter = (product, filter, value) => {
  if (filter.matchFn) return filter.matchFn(product, value);
  const v = get(product, filter.field);
  if (filter.type === 'arrayIncludes') return Array.isArray(v) && v.includes(value);
  return String(v ?? '') === String(value);
};
